import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUsers, FaFileAlt, FaUserShield, FaUserCheck, FaUserTimes, FaTrash, FaSpinner, FaCrown, FaUser, FaChartBar, FaFilter, FaPlus, FaHistory, FaSync, FaArrowUp, FaArrowDown, FaEye, FaEyeSlash, FaKey, FaLock, FaUnlock, FaCheck, FaTimes, FaEdit } from 'react-icons/fa';
import MetricCard from './MetricCard';
import CategoryChip from './CategoryChip';
import ActionButton from './ActionButton';
import SectionHeader from './SectionHeader';
import EntryForm from './EntryForm';
import EntryList from './EntryList';
import API_URLS from '../apiUrls';

const AdminDashboard = ({ darkMode, entries, fetchEntries, loading }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  
  // Category permission management states
  const [allCategories, setAllCategories] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ===== Users Management Functions =====
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setError('');
      const res = await axios.get(API_URLS.ADMIN_USERS);
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
      else if (err.response?.status === 401) setError('Please log in to access admin features.');
      else setError('Error fetching users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      setError('');
      await axios.put(API_URLS.ADMIN_USERS, { id: userId, role: 'user' });
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
      else setError('Error approving user. Please try again.');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setError('');
        await axios.delete(API_URLS.ADMIN_USERS, { data: { id: userId } });
        fetchUsers();
      } catch (err) {
        if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
        else setError('Error deleting user. Please try again.');
      }
    }
  };

  // ===== Category Permission Management Functions =====
  const fetchAllCategories = async () => {
    try {
      const res = await axios.get(API_URLS.CATEGORY_MANAGEMENT);
      setAllCategories(res.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const openPermissionModal = async (user) => {
    setSelectedUser(user);
    setShowPermissionModal(true);
    await fetchAllCategories();
  };

  const grantPermission = async (userId, category) => {
    try {
      setPermissionLoading(true);
      setError('');
      await axios.post(API_URLS.USER_PERMISSIONS, { user_id: userId, category });
      fetchUsers(); // Refresh users to get updated permissions
      setError('');
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Permission already exists');
      } else {
        setError('Error granting permission. Please try again.');
      }
    } finally {
      setPermissionLoading(false);
    }
  };

  const revokePermission = async (userId, category) => {
    try {
      setPermissionLoading(true);
      setError('');
      await axios.delete(API_URLS.USER_PERMISSIONS, { data: { user_id: userId, category } });
      fetchUsers(); // Refresh users to get updated permissions
      setError('');
    } catch (err) {
      setError('Error revoking permission. Please try again.');
    } finally {
      setPermissionLoading(false);
    }
  };

  const createAndGrantCategory = async (userId, categoryName) => {
    if (!categoryName.trim()) {
      setError('Please enter a valid category name');
      return;
    }

    try {
      setCreatingCategory(true);
      setError('');
      
      // First create the category using the dedicated API
      await axios.post(API_URLS.CATEGORY_MANAGEMENT, {
        category: categoryName.trim()
      });
      
      // Then grant permission to the user
      await axios.post(API_URLS.USER_PERMISSIONS, { 
        user_id: userId, 
        category: categoryName.trim() 
      });

      // Refresh data
      fetchUsers();
      fetchAllCategories();
      
      // Reset form
      setNewCategory('');
      setShowNewCategoryInput(false);
      setError('');
      setSuccessMessage(`Category "${categoryName.trim()}" created and granted successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Category already exists or permission already granted');
      } else {
        setError('Error creating category. Please try again.');
      }
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleNewCategoryKeyPress = (e) => {
    if (e.key === 'Enter' && newCategory.trim() && !creatingCategory) {
      createAndGrantCategory(selectedUser.id, newCategory);
    }
  };

  // ===== Entries Management Functions =====
  const deleteEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await axios.delete(API_URLS.ENTRIES, { data: { id: entryId } });
        fetchEntries();
      } catch (err) {
        setError('Error deleting entry');
      }
    }
  };

  const updateEntry = async (entryId, updatedData) => {
    try {
      await axios.put(API_URLS.ENTRIES, { id: entryId, ...updatedData });
      fetchEntries();
    } catch (err) {
      setError('Error updating entry');
    }
  };

  useEffect(() => { 
    if (activeTab === 'users') {
      fetchUsers();
      fetchAllCategories();
    }
  }, [activeTab]);

  // ===== Filtering & Summary Logic for Entries =====
  const categories = Array.from(new Set(entries.map((entry) => entry.category)));
  const filteredEntries = entries.filter((entry) => {
    const matchCategory = selectedCategory === 'All' || entry.category === selectedCategory;
    const matchType = selectedType === 'All' || entry.type === selectedType;
    let matchDate = true;
    if (startDate && entry.created_at < startDate) matchDate = false;
    if (endDate && entry.created_at > endDate) matchDate = false;
    const matchSearch = !searchTerm || entry.description?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.category?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.amount?.toString().includes(searchTerm);
    return matchCategory && matchType && matchDate && matchSearch;
  });
  const totalIncoming = filteredEntries.reduce((sum, entry) => entry.type === 'incoming' ? sum + parseFloat(entry.amount || 0) : sum, 0);
  const totalOutgoing = filteredEntries.reduce((sum, entry) => entry.type === 'outgoing' ? sum + parseFloat(entry.amount || 0) : sum, 0);
  const netBalance = totalIncoming - totalOutgoing;

  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedType('All');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };
  const hasActiveFilters = selectedCategory !== 'All' || selectedType !== 'All' || startDate || endDate || searchTerm;

  const getCategoryBgColor = category => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-orange-100 text-orange-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-teal-100 text-teal-700',
      'bg-red-100 text-red-700'
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Header */}
        <SectionHeader icon={FaCrown} title='Cashbook Admin' className='mb-4'>
          <div className='flex gap-2'>
            <MetricCard icon={FaUsers} title='Total Users' value={users.length} bgColor='bg-white dark:bg-gray-800' className='p-3 text-base' />
            <MetricCard icon={FaFileAlt} title='Total Entries' value={entries.length} bgColor='bg-white dark:bg-gray-800' className='p-3 text-base' />
            <ActionButton icon={FaSync} onClick={fetchEntries} variant='outline' className='px-3 py-2 text-sm'>Refresh</ActionButton>
          </div>
        </SectionHeader>
        <div className='mb-8 flex gap-4'>
          <ActionButton icon={FaUserShield} onClick={() => setActiveTab('users')} variant={activeTab === 'users' ? 'primary' : 'outline'} className='px-4 py-2 text-sm'>{'Manage Users'}</ActionButton>
          <ActionButton icon={FaFileAlt} onClick={() => setActiveTab('entries')} variant={activeTab === 'entries' ? 'primary' : 'outline'} className='px-4 py-2 text-sm'>{'Manage Entries'}</ActionButton>
        </div>
        {error && <div className='mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 rounded-xl text-red-700 dark:text-red-300 text-sm'>{error}</div>}
        {activeTab === 'users' && (
          <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-200 dark:border-gray-700'>
            <SectionHeader icon={FaUserShield} title='User Management' />
            {usersLoading ? (
              <div className='flex justify-center py-12'>
                <FaSpinner className='animate-spin h-8 w-8 text-purple-500 mb-4' />
                <p className='text-gray-600 dark:text-gray-400 ml-4'>Loading users...</p>
              </div>
            ) : users.length > 0 ? (
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                  <thead className='bg-gradient-to-r from-purple-600 to-pink-600'>
                    <tr>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider'><FaUser className='inline mr-2' />ID</th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider'>Username</th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider'>Role</th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider'>Category Permissions</th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                    {users.map((user) => (
                      <tr key={user.id} className='hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200'>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>#{user.id}</td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white'>
                          <div className='flex items-center'>
                            <div className='p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mr-3'><FaUser className='h-4 w-4 text-gray-600 dark:text-gray-400' /></div>
                            {user.username}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm'>
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : user.role === 'user' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
                            {user.role === 'admin' && <FaCrown className='h-3 w-3 mr-1' />}
                            {user.role}
                          </span>
                        </td>
                        <td className='px-6 py-4 text-sm'>
                          <div className='flex flex-wrap gap-1'>
                            {user.categories && user.categories.length > 0 ? (
                              user.categories.map((category, index) => (
                                <span key={index} className={`px-2 py-1 text-xs rounded-full ${getCategoryBgColor(category)}`}>
                                  {category}
                                </span>
                              ))
                            ) : (
                              <span className='text-gray-400 text-xs'>No permissions</span>
                            )}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm'>
                          <div className='flex space-x-2'>
                            {user.role === 'pending' && (
                              <ActionButton icon={FaUserCheck} onClick={() => approveUser(user.id)} variant='primary' className='px-3 py-1 text-xs'>Approve</ActionButton>
                            )}
                            <ActionButton icon={FaKey} onClick={() => openPermissionModal(user)} variant='outline' className='px-3 py-1 text-xs'>Permissions</ActionButton>
                            <ActionButton icon={FaUserTimes} onClick={() => deleteUser(user.id)} variant='outline' className='px-3 py-1 text-xs'>Delete</ActionButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='text-center py-12'>
                <div className='mx-auto h-16 w-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4'><FaUsers className='h-8 w-8 text-gray-400' /></div>
                <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>No users found</h3>
                <p className='text-gray-500 dark:text-gray-400 max-w-md mx-auto'>{error ? 'Unable to load users due to permission issues.' : 'No users are currently registered.'}</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'entries' && (
          <>
            <SectionHeader icon={FaFilter} title='Search & Filters' className='mb-2'>
              <ActionButton icon={FaFilter} onClick={() => setShowFilters(!showFilters)} variant={showFilters ? 'primary' : 'outline'} className='px-3 py-2 text-sm'>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </ActionButton>
            </SectionHeader>
            {showFilters && (
              <div className='mb-4 grid grid-cols-1 md:grid-cols-5 gap-2'>
                <div>
                  <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>Category</label>
                  <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'>
                    <option value='All'>All Categories</option>
                    {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                  </select>
                </div>
                <div>
                  <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>Type</label>
                  <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'>
                    <option value='All'>All Types</option>
                    <option value='incoming'>Incoming</option>
                    <option value='outgoing'>Outgoing</option>
                  </select>
                </div>
                <div>
                  <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>From Date</label>
                  <input type='date' value={startDate} onChange={e => setStartDate(e.target.value)} className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white' />
                </div>
                <div>
                  <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>To Date</label>
                  <input type='date' value={endDate} onChange={e => setEndDate(e.target.value)} className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white' />
                </div>
                <div>
                  <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>Search</label>
                  <input type='text' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white' placeholder='Search by description, category, or amount' />
                </div>
                {hasActiveFilters && (
                  <div className='col-span-5 flex justify-end'>
                    <ActionButton onClick={clearFilters} variant='outline' className='px-3 py-1.5 text-sm'>Clear All Filters</ActionButton>
                  </div>
                )}
              </div>
            )}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
              <MetricCard icon={FaArrowUp} title='Total Income' value={`Rs.${totalIncoming.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color='text-green-600' bgColor='bg-green-50 dark:bg-green-900/20' iconBg='bg-green-200 dark:bg-green-900/40' percent={'+100% of total'} subtext='All incoming transactions' className='p-3 text-base' />
              <MetricCard icon={FaArrowDown} title='Total Expenses' value={`Rs.${totalOutgoing.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color='text-red-600' bgColor='bg-red-50 dark:bg-red-900/20' iconBg='bg-red-200 dark:bg-red-900/40' percent={'-0% of total'} subtext='All outgoing transactions' className='p-3 text-base' />
              <MetricCard icon={FaChartBar} title='Net Balance' value={`Rs.${netBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color='text-blue-600' bgColor='bg-blue-50 dark:bg-blue-900/20' iconBg='bg-blue-200 dark:bg-blue-900/40' percent={'+100% margin'} subtext={netBalance >= 0 ? 'Positive balance' : 'Negative balance'} highlight={netBalance >= 0} className='p-3 text-base' />
            </div>
            {/* Add New Transaction Section */}
            <SectionHeader icon={FaPlus} title='Add New Transaction'>
              <ActionButton icon={isFormVisible ? FaEyeSlash : FaEye} onClick={() => setIsFormVisible(!isFormVisible)} variant='primary' className='px-4 py-2 text-sm'>
                {isFormVisible ? 'Hide Form' : 'Show Form'}
              </ActionButton>
            </SectionHeader>
            {isFormVisible && (
              <div className='mb-6'>
                <EntryForm fetchEntries={fetchEntries} darkMode={darkMode} />
              </div>
            )}
            <SectionHeader icon={FaHistory} title='Transaction History' />
            <EntryList entries={filteredEntries} deleteEntry={deleteEntry} updateEntry={updateEntry} isAdmin={true} darkMode={darkMode} />
          </>
        )}

        {/* Category Permission Management Modal */}
        {showPermissionModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Manage Permissions for {selectedUser.username}
                </h3>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-3 rounded-lg text-green-700 dark:text-green-300 text-sm">
                  <div className="flex items-center">
                    <FaCheck className="w-4 h-4 mr-2" />
                    {successMessage}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Current Permissions */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <FaLock className="mr-2" />
                    Current Permissions
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.categories && selectedUser.categories.length > 0 ? (
                      selectedUser.categories.map((category, index) => (
                        <div key={index} className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-2 rounded-lg">
                          <FaCheck className="h-4 w-4" />
                          <span className="text-sm font-medium">{category}</span>
                          <button
                            onClick={() => revokePermission(selectedUser.id, category)}
                            disabled={permissionLoading}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-2"
                          >
                            <FaTimes className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No category permissions granted</p>
                    )}
                  </div>
                </div>

                {/* Available Categories */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <FaUnlock className="mr-2" />
                    Available Categories
                  </h4>
                  
                  {/* New Category Creation */}
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 flex items-center">
                        <FaPlus className="mr-2" />
                        Create New Category
                      </h5>
                      <button
                        onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                      >
                        {showNewCategoryInput ? 'Cancel' : 'Add New'}
                      </button>
                    </div>
                    
                    {showNewCategoryInput && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            New Category Name
                          </label>
                          <input
                            type="text"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            onKeyPress={handleNewCategoryKeyPress}
                            placeholder="Enter category name..."
                            className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={creatingCategory}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => createAndGrantCategory(selectedUser.id, newCategory)}
                            disabled={creatingCategory || !newCategory.trim()}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            {creatingCategory ? (
                              <div className="flex items-center justify-center">
                                <FaSpinner className="animate-spin h-4 w-4 mr-2" />
                                Creating...
                              </div>
                            ) : (
                              'Create & Grant Permission'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {allCategories
                      .filter(category => !selectedUser.categories?.includes(category))
                      .map((category, index) => (
                        <button
                          key={index}
                          onClick={() => grantPermission(selectedUser.id, category)}
                          disabled={permissionLoading}
                          className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-left"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{category}</span>
                          <FaPlus className="h-4 w-4 text-gray-500" />
                        </button>
                      ))}
                  </div>
                  {allCategories.filter(category => !selectedUser.categories?.includes(category)).length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">All categories already granted</p>
                  )}
                </div>

                {/* Loading State */}
                {permissionLoading && (
                  <div className="flex items-center justify-center py-4">
                    <FaSpinner className="animate-spin h-6 w-6 text-purple-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Updating permissions...</span>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <ActionButton
                  onClick={() => setShowPermissionModal(false)}
                  variant="outline"
                  className="px-4 py-2"
                >
                  Close
                </ActionButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
